import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Play, Lock, ChevronDown, Clock, Sparkles, RefreshCw } from "lucide-react";
import { getIcon } from "@/lib/icons";

function VideoRow({ v, watched, onOpen }) {
  return (
    <button
      data-testid={`roadmap-video-${v.video_id}`}
      onClick={() => onOpen(v.video_id)}
      className="group/video w-full flex items-center gap-4 p-4 border-t border-border/60 hover:bg-accent/40 transition-colors text-left"
    >
      <div className="relative w-28 h-16 shrink-0 rounded-lg overflow-hidden bg-secondary">
        {v.thumbnail ? (
          <img src={v.thumbnail} alt="" loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full grid place-items-center"><Play className="h-4 w-4 text-muted-foreground" /></div>
        )}
        <span className="absolute inset-0 grid place-items-center opacity-0 group-hover/video:opacity-100 transition-opacity bg-foreground/30">
          <Play className="h-4 w-4 text-white fill-white" />
        </span>
        {v.duration_label && (
          <span className="absolute bottom-1 right-1 text-[10px] font-semibold bg-black/75 text-white rounded px-1">{v.duration_label}</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm md:text-base text-foreground line-clamp-2 group-hover/video:text-primary transition-colors">
          {v.is_new && (
            <span data-testid={`video-new-badge-${v.video_id}`} className="inline-flex items-center align-middle mr-2 px-1.5 py-0.5 rounded-md bg-pink-600/10 text-pink-600 dark:text-pink-400 text-[10px] font-bold tracking-wide">
              NEW
            </span>
          )}
          {v.video_title}
        </p>
        <p className="text-xs text-muted-foreground mt-1">{v.channel}</p>
      </div>
      {watched
        ? <Check className="h-5 w-5 text-emerald-500 shrink-0" />
        : <Play className="h-4 w-4 text-muted-foreground shrink-0 group-hover/video:text-primary transition-colors" />}
    </button>
  );
}

function ModuleRow({ item, number, isNext, open, onToggle, languageLabel, watchedSet, onSelect }) {
  const { completed, locked } = item;
  const Icon = getIcon(item.icon);
  const videos = item.videos || [];
  const n = videos.length;

  const numClasses = completed
    ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
    : isNext
    ? "bg-primary text-primary-foreground"
    : "bg-secondary text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary";

  return (
    <div className={`rounded-2xl border bg-card overflow-hidden transition-all mb-3 ${
      open ? "border-border shadow-md" : "border-border hover:border-primary/30 hover:shadow-sm"
    } ${isNext && !open ? "ring-1 ring-primary/30" : ""}`}>
      <button
        data-testid={`roadmap-node-${item.topic_slug}`}
        disabled={locked}
        onClick={onToggle}
        className={`group w-full flex items-center gap-3 md:gap-4 p-4 md:p-5 text-left ${locked ? "cursor-not-allowed opacity-70" : "cursor-pointer"}`}
      >
        <span className={`grid place-items-center h-10 w-10 shrink-0 rounded-full text-sm font-bold transition-colors ${numClasses}`}>
          {completed ? <Check className="h-4 w-4" /> : locked ? <Lock className="h-3.5 w-3.5" /> : number}
        </span>
        <span className="hidden sm:grid place-items-center h-10 w-10 shrink-0 rounded-xl bg-secondary text-foreground">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-heading font-bold text-base md:text-lg text-foreground">{item.topic_title}</h4>
            {isNext && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary">Next up</span>}
            {completed && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">Done</span>}
            {item.has_new && (
              <span data-testid={`module-new-badge-${item.topic_slug}`} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-pink-600/10 text-pink-600 dark:text-pink-400 inline-flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> New video
              </span>
            )}
            {item.was_upgraded && (
              <span data-testid={`module-upgraded-badge-${item.topic_slug}`} className="text-xs font-semibold px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-400 inline-flex items-center gap-1">
                <RefreshCw className="h-3 w-3" /> Updated
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">
            {locked ? `Coming soon in ${languageLabel}` : `${n} ${n === 1 ? "lesson" : "lessons"}`}
          </p>
        </div>
        {!locked && (
          <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }} className="shrink-0 text-muted-foreground">
            <ChevronDown className="h-5 w-5" />
          </motion.span>
        )}
      </button>

      <AnimatePresence initial={false}>
        {open && !locked && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
            data-testid={`roadmap-videos-${item.topic_slug}`}
          >
            <p className="px-5 py-3 text-sm text-muted-foreground bg-secondary/40 border-t border-border/60 flex items-center gap-2">
              <Clock className="h-3.5 w-3.5" /> {n} {n === 1 ? "explainer" : "explainers"} — different teachers &amp; angles
            </p>
            {videos.map((v) => (
              <VideoRow key={v.video_id} v={v} watched={watchedSet.has(v.video_id)} onOpen={onSelect} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function RoadmapGraph({ levels, nextVideoId, languageLabel = "your language", watchedVideoIds = [], onSelect, forceOpenSlug = null }) {
  const watchedSet = new Set(watchedVideoIds);
  let nextSlug = null;
  levels.forEach((lvl) => lvl.items.forEach((it) => {
    if (it.video_id && it.video_id === nextVideoId) nextSlug = it.topic_slug;
  }));
  const [openSlug, setOpenSlug] = useState(forceOpenSlug || nextSlug);

  let number = 0;
  return (
    <div className="space-y-10" data-testid="roadmap-graph">
      {levels.map((lvl) => {
        const done = lvl.items.filter((i) => i.completed).length;
        return (
          <section key={lvl.level} data-testid={`roadmap-level-${lvl.level}`}>
            <div className="border-b border-border pb-3 mb-5">
              <div className="flex items-center justify-between">
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-primary">Level {lvl.level} · {lvl.label}</p>
                <span className="text-sm text-muted-foreground font-medium">{done}/{lvl.items.length} done</span>
              </div>
              <div className="mt-2.5 h-1.5 rounded-full bg-secondary overflow-hidden">
                <div className="h-full rounded-full bg-primary transition-all duration-700"
                  style={{ width: `${lvl.items.length ? Math.round((done / lvl.items.length) * 100) : 0}%` }} />
              </div>
            </div>
            {lvl.items.map((item) => {
              number += 1;
              const isNext = item.video_id && item.video_id === nextVideoId;
              return (
                <ModuleRow
                  key={item.topic_slug}
                  item={item}
                  number={number}
                  isNext={isNext}
                  open={openSlug === item.topic_slug}
                  onToggle={() => setOpenSlug((s) => (s === item.topic_slug ? null : item.topic_slug))}
                  languageLabel={languageLabel}
                  watchedSet={watchedSet}
                  onSelect={onSelect}
                />
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
